import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useMasters } from '../../hooks/useMasters';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, money, weight } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Stat, Table } from '../../components/ui';
import { Select } from '../../components/ui/Form';

interface Lot {
  id: string;
  lot_code: string;
  status: string;
  received_at: string;
  source_doc_type: string | null;
  source_doc_no: string | null;
  source_doc_id: string | null;
  received_fine_wt: string;
  remaining_wt: string;
  remaining_fine_wt: string;
  cost_per_fine_gram: string;
  remaining_value: string;
  purity_code: string;
  metal_id: string;
  metal_code: string;
  metal_name: string;
  branch_name: string;
  product_name: string;
}

const SOURCE: Record<string, string> = {
  OLD_GOLD: 'Bought from a customer',
  MELTING_RECOVERY: 'Dust from a melt',
  MELTING: 'Bullion from a melt',
};

const n = (v: string | null | undefined): number => Number(v || 0);

/**
 * Old metal waiting to be melted: what was bought back over the counter, the
 * dust recovered from the crucible, bullion lots from earlier melts — per
 * metal, at what it cost, and how long it has been sitting.
 */
export function MeltLotsPage() {
  const { can } = usePermission();
  const navigate = useNavigate();
  const masters = useMasters();
  const [metalId, setMetalId] = useState('');
  const [all, setAll] = useState(false);

  const q = useQuery({
    queryKey: ['melt-lots', metalId, all],
    queryFn: () =>
      api.get<{ data: Lot[] }>(
        `/meltings/lots?${new URLSearchParams({ ...(metalId ? { metalId } : {}), ...(all ? { all: 'true' } : {}) })}`,
      ),
  });
  const lots = q.data?.data ?? [];

  // One summary card per metal: grams of pure metal waiting, and their cost.
  const perMetal = new Map<string, { name: string; fine: number; value: number; lots: number }>();
  for (const l of lots.filter((x) => x.status === 'OPEN')) {
    const m = perMetal.get(l.metal_id) ?? {
      name: l.metal_name,
      fine: 0,
      value: 0,
      lots: 0,
    };
    m.fine += n(l.remaining_fine_wt);
    m.value += n(l.remaining_value);
    m.lots += 1;
    perMetal.set(l.metal_id, m);
  }
  const days = (d: string): number => Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);

  return (
    <>
      <PageHeader
        title="Old metal and scrap"
        subtitle="Bought back, recovered or left over — weighed stock waiting to go into a melt."
        actions={
          <div className="flex gap-2">
            <Link className="btn-ghost" to="/old-gold">
              Buy old metal
            </Link>
            {can('melting.create') ? (
              <button className="btn-primary" onClick={() => navigate(`/melting?send=${metalId}`)}>
                Send for melting
              </button>
            ) : null}
          </div>
        }
      />
      <ErrorNotice error={q.error} />

      {perMetal.size > 0 ? (
        <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[...perMetal].map(([id, m]) => (
            <Stat
              key={id}
              label={`${m.name} waiting`}
              value={fine(m.fine.toFixed(4))}
              hint={`${m.lots} lot${m.lots === 1 ? '' : 's'} · ${money(m.value.toFixed(2))} at cost`}
            />
          ))}
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-56">
          <p className="label">Metal</p>
          <Select value={metalId} onChange={setMetalId} options={masters.metalOptions} placeholder="All metals" />
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Include lots already melted
        </label>
      </div>

      {q.isLoading ? <Loading /> : null}
      {!q.isLoading && lots.length === 0 ? (
        <Empty
          message="No old metal waiting."
          hint="Old gold and silver bought from customers, and dust recovered from melts, appear here."
        />
      ) : null}
      {lots.length > 0 ? (
        <Table
          head={[
            'Received',
            'Days',
            'Lot',
            'Metal',
            'Purity',
            'From',
            'Branch',
            'Weight',
            'Fine left',
            'Cost / fine g',
            'Value',
            '',
          ]}
        >
          {lots.map((l) => (
            <tr key={l.id} className={l.status !== 'OPEN' ? 'text-ink-soft' : ''}>
              <td className="td">{date(l.received_at)}</td>
              <td
                className={`td num text-right ${days(l.received_at) > 30 && l.status === 'OPEN' ? 'font-semibold text-warn' : ''}`}
              >
                {days(l.received_at)}
              </td>
              <td className="td num">{l.lot_code}</td>
              <td className="td">{l.metal_name}</td>
              <td className="td num">{l.purity_code}</td>
              <td className="td text-xs">
                {SOURCE[l.source_doc_type ?? ''] ?? l.product_name}
                {l.source_doc_no ? <span className="num ml-1 text-ink-soft">{l.source_doc_no}</span> : null}
              </td>
              <td className="td">{l.branch_name}</td>
              <td className="td num text-right">{weight(l.remaining_wt)}</td>
              <td className="td num text-right">{fine(l.remaining_fine_wt)}</td>
              <td className="td num text-right">{money(l.cost_per_fine_gram)}</td>
              <td className="td num text-right">{money(l.remaining_value)}</td>
              <td className="td text-right">
                {l.status === 'OPEN' && can('melting.create') ? (
                  <button
                    className="btn-ghost px-2 py-1 text-xs"
                    onClick={() => navigate(`/melting?send=${l.metal_id}`)}
                  >
                    Melt
                  </button>
                ) : (
                  <Chip tone="mute">{l.status === 'EXHAUSTED' ? 'melted' : l.status.toLowerCase()}</Chip>
                )}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}
    </>
  );
}
